import React, { useState, useEffect, useRef, useContext } from 'react';
import { 
    View, Text, Animated, PanResponder, TouchableOpacity, 
    Image, BackHandler 
} from 'react-native';
import { useFocusEffect, useIsFocused } from '@react-navigation/native';
import { CounterRatiosContext } from './CounterRatiosContext';
import { styles } from './components/AppStyles'; // Import styles from AppStyles.js
import { CorrectButton, WrongButton, ToggleButton } from './components/StyledButtons.js';
import AsyncStorage from '@react-native-async-storage/async-storage';

const GroupScreen = ({ route, navigation }) => {
  const { counterRatios, setCounterRatios } = React.useContext(CounterRatiosContext);
  const { 
    screens: passedScreens, 
    currentIndex: initialIndex, 
    choice 
  } = route.params;
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [screens] = useState(passedScreens);  
  const currentGroupData = counterRatios[route.name] || {};
  const [counter1, setCounter1] = useState(currentGroupData.counter1 || 0);
  const [counter2, setCounter2] = useState(currentGroupData.counter2 || 0);  
  const counter1Ref = useRef(counter1);
  const counter2Ref = useRef(counter2);  
  const [areNavButtonsVisible, setAreNavButtonsVisible] = useState(false);
  const isFocused = useIsFocused();
  const [isToggleButtonVisible, setToggleButtonVisible] = useState(true);
  const [showFeedback, setShowFeedback] = useState(null); 
  const feedbackOpacity = useState(new Animated.Value(0))[0];
  const [feedbackColor, setFeedbackColor] = useState(null);
  const swipeDirectionHistoryRef = useRef([]);
  const [position] = useState(new Animated.ValueXY());
  const [isVisible] = useState(new Animated.Value(0));
  const [showToggleButtonOpacity] = useState(new Animated.Value(1));
  const [buttonsOpacity] = useState(new Animated.Value(0)); 
  let mainText, subText, mainLabel, subLabel;
  const groupIndex = route.params.index;

  // Function to load swipe history for the current group
  const loadSwipeHistory = async () => {
    try {
        const swipeHistoryKey = `swipeHistory_${route.name}`;
        const storedHistory = await AsyncStorage.getItem(swipeHistoryKey);

        // Check if storedHistory is null or undefined, and initialize swipeDirectionHistoryRef.current as an empty array if it is
        const swipeHistory = storedHistory ? JSON.parse(storedHistory) : [];
        swipeDirectionHistoryRef.current = swipeHistory;
    } catch (error) {
        //console.error("Failed to load swipe history:", error);
    }
  };

  // Function to update and save the swipe history when a swipe occurs
  const updateAndSaveSwipeHistory = async (direction) => {
    try {
      swipeDirectionHistoryRef.current.push(direction);

      const swipeHistoryKey = `swipeHistory_${route.name}`;
      await AsyncStorage.setItem(swipeHistoryKey, JSON.stringify(swipeDirectionHistoryRef.current));
    } catch (error) {
      //console.error("Failed to update and save swipe history:", error);
    }
  };

  // Function to reverse the last swipe direction from AsyncStorage
  const reverseLastSwipe = async () => {
    try {
        const lastDirection = swipeDirectionHistoryRef.current.pop();

        if (lastDirection === 'right') {
            // Decrement Counter1
            setCounter1((prevCounter1) => prevCounter1 - 1);
        } else if (lastDirection === 'left') {
            // Decrement Counter2
            setCounter2((prevCounter2) => prevCounter2 - 1);
        } else {
            //console.log("Error: Last swipe direction data was missing / ", lastDirection);
        }

        const swipeHistoryKey = `swipeHistory_${route.name}`;
        await AsyncStorage.setItem(swipeHistoryKey, JSON.stringify(swipeDirectionHistoryRef.current));

        return lastDirection; // Return the lastDirection
    } catch (error) {
        //console.error("Failed to reverse last swipe direction:", error);
    }
  };


  // Function to clear the swipe history for the current group
  const clearSwipeHistory = async () => {
    try {
      const swipeHistoryKey = `swipeHistory_${route.name}`;
      await AsyncStorage.removeItem(swipeHistoryKey);
    } catch (error) {
      //console.error("Failed to clear swipe history:", error);
    }
  };

  useEffect(() => {
    // Load swipe history when the GroupScreen mounts
    loadSwipeHistory();
  }, []);

  useEffect(() => {
    return () => {
      // Save the state data to AsyncStorage when the component unmounts
      saveScreenStateToAsyncStorage();
    };
  }, []);
  
  const saveScreenStateToAsyncStorage = async () => {
    try {
      const groupStateKey = `groupState_${route.name}`;
      const stateData = {
        choice: choice,
        counter1: counter1Ref.current,
        counter2: counter2Ref.current,
        lastScreenIndex: swipeDirectionHistoryRef.current.length,
        swipeHistory: swipeDirectionHistoryRef.current,
      };
      await AsyncStorage.setItem(groupStateKey, JSON.stringify(stateData));
      console.log("Save: ", groupStateKey, stateData);
    } catch (error) {
     //console.error("Failed to save group state:", error);
    }
  };  


useFocusEffect(
  React.useCallback(() => {
    const onBackPress = () => {
      //console.log('Full History: ', swipeDirectionHistoryRef.current);
      const lastSwipeDirection = reverseLastSwipe();
      //console.log('Last Direction: ', lastSwipeDirection);          
      if (currentIndex === 0) {
        setCounterRatios(prevRatios => {
          const updatedRatios = {
              ...prevRatios,
              [route.name]: {
                  ...prevRatios[route.name],
                  lastScreenIndex: 0,
                  choice: null,
              }
          };
          return updatedRatios;
        });
    
        navigation.navigate('Home');
      } else {    

        if (lastSwipeDirection === 'right') {
          // Decrement Counter1
          setCounter1((prevCounter1) => prevCounter1 - 1);
        } else if (lastSwipeDirection === 'left') {
          // Decrement Counter2
          setCounter2((prevCounter2) => prevCounter2 - 1);
        } else {
          //console.log("Error: Last swipe direction data was missing / ", lastSwipeDirection);
        }
        setCurrentIndex(prevIndex => prevIndex - 1);
        setCounterRatios(prevRatios => {
          const updatedRatios = {
              ...prevRatios,
              [route.name]: {
                  ...prevRatios[route.name],
                  lastScreenIndex: swipeDirectionHistoryRef.current.length,
                  counter1: counter1Ref.current,
                  counter2: counter2Ref.current,
                  swipeHistory: swipeDirectionHistoryRef.current,
              }
          };
          return updatedRatios;
        }); 
      }
      saveScreenStateToAsyncStorage();
      return true;
    };      

    navigation.setOptions({
      headerLeft: () => (
        <TouchableOpacity 
          onPress={onBackPress}
          style={styles.headerButton}
        >
          <Text style={styles.headerButtonText}>&#10094; Back</Text>
        </TouchableOpacity>
      )
    });
    
    BackHandler.addEventListener('hardwareBackPress', onBackPress);
    return () => {
      BackHandler.removeEventListener('hardwareBackPress', onBackPress);
    };
  }, [navigation, currentIndex, setCounterRatios])
);

  const handleHomeButtonPress = () => {
    setCounterRatios(prevRatios => {
        const updatedRatios = {
            ...prevRatios,
            [route.name]: {
                ...prevRatios[route.name],
                lastScreenIndex: currentIndex,
                counter1: counter1Ref.current,
                counter2: counter2Ref.current,
                swipeHistory: swipeDirectionHistoryRef.current,
            }
        };
        return updatedRatios;
    });

    navigation.popToTop(); // Always navigate to the HomeScreen
  };

  React.useLayoutEffect(() => {
    if (currentIndex !== 0 && currentIndex !== screens.length - 1) {
      navigation.setOptions({
        headerRight: () => (
          <TouchableOpacity onPress={handleHomeButtonPress} style={styles.homeButton}>
            <Image style={styles.homeIcon} source={require('./assets/icon_home.png')} />
          </TouchableOpacity>
        )
      });
    } else {
      navigation.setOptions({
        headerRight: null
      });
    }
  }, [navigation, currentIndex]);

  const handleSwipe = (direction) => {
    const isRightSwipe = direction === 'right';
    setShowFeedback(isRightSwipe ? 'Correct!' : 'Incorrect!');
    setFeedbackColor(isRightSwipe ? '#41ad49' : '#d1444a');
    updateAndSaveSwipeHistory(direction);
  
    Animated.sequence([
      Animated.timing(feedbackOpacity, {
        toValue: 1,
        duration: 200,
        useNativeDriver: true,
      }),
      Animated.delay(300),
      Animated.timing(feedbackOpacity, {
        toValue: 0,
        duration: 200,
        useNativeDriver: true,
      }),
    ]).start(() => {
      setShowFeedback(null);
  
      if (isRightSwipe) {
        setCounter1(counter1 + 1);
      } else {
        setCounter2(counter2 + 1);
      }
  
      resetVisibilityStates();
  
      if (currentIndex < screens.length - 1) {
        setCurrentIndex((prevIndex) => prevIndex + 1);
        setCounterRatios(prevRatios => {
          const updatedRatios = {
              ...prevRatios,
              [route.name]: {
                  ...prevRatios[route.name],
                  lastScreenIndex: swipeDirectionHistoryRef.current.length,
                  counter1: counter1Ref.current,
                  counter2: counter2Ref.current,
                  swipeHistory: swipeDirectionHistoryRef.current,
              }
          };
          return updatedRatios;
        });     
        saveScreenStateToAsyncStorage();
      } else {
        const newCounter1 = isRightSwipe ? counter1 + 1 : counter1;
        const newCounter2 = !isRightSwipe ? counter2 + 1 : counter2;
        const percentage = isRightSwipe
          ? ((newCounter1 / screens.length) * 100).toFixed(2)
          : (((screens.length - newCounter2) / screens.length) * 100).toFixed(2);
  
        // Update the progress data for this group
        setCounterRatios((prevRatios) => ({
          ...prevRatios,
          [route.name]: {
            lastScreenIndex: currentIndex,
            counter1: newCounter1,
            counter2: newCounter2,
            percentage,
            choice,
          },
        }));
  
        // Clear swipe history here before navigating
        clearSwipeHistory();
  
        // Check if the percentage is 70 or above to determine the screen to navigate to
        const percentageScreenColor = percentage >= 70 ? '#41ad49' : '#d1444a';
  
        // Navigate to PercentageScreen with the appropriate background color
        navigation.navigate('Percentage', {
          percentage,
          index: currentIndex + 1,
          counter1: newCounter1,
          counter2: newCounter2,
          groupIndex: groupIndex
        });
      }
    });
  };  

  const handleRightSwipe = () => handleSwipe('right');
  const handleLeftSwipe = () => handleSwipe('left');
  position.setValue({ x: 0, y: 0 });


  const panResponder = PanResponder.create({
  onStartShouldSetPanResponder: (e, gestureState) => {
    // Check if the movement is minimal, which might indicate a tap
    const isTapLikeGesture = Math.abs(gestureState.dx) < 10 && Math.abs(gestureState.dy) < 10;
    return areNavButtonsVisible && !isTapLikeGesture;
  },
  onMoveShouldSetPanResponder: (e, gestureState) => {
    // Check if the movement is more than a tap
    const isTapLikeGesture = Math.abs(gestureState.dx) < 10 && Math.abs(gestureState.dy) < 10;
    return areNavButtonsVisible && !isTapLikeGesture;
  },
  onPanResponderMove: Animated.event(
    [
      null,
      {
        dx: position.x, // delta x of movement
      },
    ],
    { useNativeDriver: false }
  ),
  onPanResponderRelease: (e, gestureState) => {
    if (gestureState.dx > 100) {
      // Right Swipe with increased threshold
      handleRightSwipe();
    } else if (gestureState.dx < -100) {
      // Left Swipe with increased threshold
      handleLeftSwipe();
    } else {
      // Reset position for non-swipe gestures
      Animated.spring(position, {
        toValue: { x: 0, y: 0 },
        useNativeDriver: false
      }).start();
    }
  },   
});

  const resetVisibilityStates = () => {
      // Reset animations
      isVisible.setValue(0);
      setAreNavButtonsVisible(false);
      setToggleButtonVisible(true);
      showToggleButtonOpacity.setValue(1);  // Reset the animated opacity value
  };

  useEffect(() => {
    setCounterRatios(prevRatios => ({
      ...prevRatios,
      [route.name]: {
        ...prevRatios[route.name],
        counter1,
        counter2
      }
    }));
  }, [counter1, counter2]);  

  useEffect(() => {
    if (!isFocused) {
        resetVisibilityStates();
    }
  }, [isFocused]);

  useEffect(() => {
    // When the GroupScreen mounts, check if we have a lastScreenIndex for this group and navigate to it
    const groupData = counterRatios[route.name] || {};
    if (groupData.lastScreenIndex != null) {
      navigation.setParams({ currentIndex: groupData.lastScreenIndex });
    }
  }, []);

  useEffect(() => {
    counter1Ref.current = counter1;
    counter2Ref.current = counter2;
  }, [counter1, counter2]);

  useEffect(() => {
    const unsubscribe = navigation.addListener('beforeRemove', (e) => {
        if (showFeedback === "Correct") {
            setCounterRatios(prevRatios => ({
                ...prevRatios,
                [route.name]: {
                    ...prevRatios[route.name],
                    ...(currentIndex - 1 > 0 ? { lastScreenIndex: currentIndex - 1 } : {}),
                    counter1: counter1Ref.current - 1
                }
            }));
        } else if (showFeedback === "Wrong") {
            setCounterRatios(prevRatios => ({
                ...prevRatios,
                [route.name]: {
                    ...prevRatios[route.name],
                    ...(currentIndex - 1 > 0 ? { lastScreenIndex: currentIndex - 1 } : {}),
                    counter2: counter2Ref.current - 1
                }
            }));
        }
    });
  
    // Cleanup the listener when the component unmounts
    return unsubscribe;
  }, [navigation, currentIndex, showFeedback, counter1, counter2]);  


  const handleToggleButtonPress = () => {
    Animated.sequence([
      Animated.timing(showToggleButtonOpacity, {
        toValue: 0,
        duration: 500,
        useNativeDriver: true
      }),
      Animated.timing(isVisible, {
        toValue: 1,
        duration: 500,
        useNativeDriver: true
      }),
      Animated.timing(buttonsOpacity, {
        toValue: 1,
        duration: 500,
        useNativeDriver: true
      })
    ]).start(() => {
      setAreNavButtonsVisible(true);
    });
  };  

  const translateY = isVisible.interpolate({
    inputRange: [0, 1],
    outputRange: [30, 0]  // Reduced distance from 50 to 30
  });
  
  const translateYButtons = isVisible.interpolate({
    inputRange: [0, 1],
    outputRange: [30, 0]  // Values can be adjusted for desired effect
  });  

  if (choice === "title") {
    mainText = screens[currentIndex].title;
    subText = screens[currentIndex].definition;
    mainLabel = "Term";
    subLabel = "Definition";
  } else {
    mainText = screens[currentIndex].definition;
    subText = screens[currentIndex].title;
    mainLabel = "Definition";
    subLabel = "Term";
  }

  return (
    <>
      <Animated.View {...panResponder.panHandlers} style={[styles.animatedView, { transform: [{ translateX: position.x }] }]}>
        <View style={styles.centeredContainer}>

          {/* Main Text with Label */}
          <View>
            <Text style={styles.labelStyle}>{mainLabel}</Text>
            <Text style={styles.boldText}>{mainText}</Text>
          </View>

          {isToggleButtonVisible && (
            <Animated.View style={[styles.toggleButtonContainer, { opacity: showToggleButtonOpacity }]}>
              <ToggleButton title="Show Answer" onPress={handleToggleButtonPress} />
            </Animated.View>
          )}

          {/* Sub Text with Label */}
          <Animated.Text style={[styles.animatedText, { transform: [{ translateY: translateY }], opacity: isVisible }]}>
            <Text style={styles.labelStyle}>{subLabel}{'\n'}</Text>
            {subText}
          </Animated.Text>
  
          <View style={styles.navButtonContainer}>
            {areNavButtonsVisible && (
              <Animated.View 
                style={[styles.navButtons, { opacity: buttonsOpacity, transform: [{ translateY: translateYButtons }] }]}
              >
                <>
                  <WrongButton onPress={() => { handleLeftSwipe(); }} />                 
                  <CorrectButton onPress={() => { handleRightSwipe(); }} />
                </>
              </Animated.View>
            )}
          </View>
  
          <Text style={styles.bottomLeftText}>Incorrect: {counter2}</Text>
          <Text style={styles.bottomRightText}>Correct: {counter1}</Text>
        </View>   
      </Animated.View>

      {/* Feedback overlay */}
      {showFeedback && (
          <View style={[styles.feedbackOverlay, { backgroundColor: 'white' }]}>
              <Text style={[styles.feedbackText, { color: feedbackColor }]}>{showFeedback}</Text>
          </View>
      )}
    </>    
  );
};

export default GroupScreen;